import { cn } from "@/lib/utils";

const TILE = { sm: "h-9 w-9 text-sm rounded-lg", md: "h-11 w-11 text-[15px] rounded-[11px]", lg: "h-14 w-14 text-lg rounded-xl" };

export function CoTile({ name, size = "md", className }) {
  return (
    <span aria-hidden="true" className={cn("shrink-0 flex items-center justify-center font-semibold bg-foreground/[0.06] text-foreground/70", TILE[size], className)}>
      {(name || "?").trim().charAt(0).toUpperCase()}
    </span>
  );
}

const STEPS = ["Applied", "Assessment", "Interview", "Offer"];

export function StageTrack({ done, muted, className }) {
  return (
    <div className={cn("grid grid-cols-4 gap-1 w-full", className)} role="img" aria-label={`Progress: ${STEPS[done - 1]}`}>
      {STEPS.map((s, i) => (
        <div key={s} className="space-y-1.5 min-w-0">
          <div className={cn("h-1 rounded-full transition-colors", i < done ? (muted ? "bg-muted-foreground/40" : "bg-primary") : "bg-foreground/[0.08]")} />
          <div className="text-[11px] text-muted-foreground text-center truncate">{s}</div>
        </div>
      ))}
    </div>
  );
}

export function FitRing({ value, size = 96, stroke = 7, label, suffix = "", testId }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value || 0)) / 100;
  return (
    <div className="flex flex-col items-center gap-1.5 shrink-0" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
          <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" className="stroke-foreground/[0.08]" />
          <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" strokeLinecap="round" className="stroke-primary"
            strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset .8s cubic-bezier(.2,.7,.2,1)" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-display font-semibold tabular-nums" style={{ fontSize: size * 0.27 }}>
          {Math.round(value || 0)}{suffix}
        </span>
      </div>
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
    </div>
  );
}

export function Panel({ title, aside, children, className, testId }) {
  return (
    <section className={cn("hs-card p-5", className)} data-testid={testId}>
      {(title || aside) && (
        <header className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          {aside}
        </header>
      )}
      {children}
    </section>
  );
}

export function Fact({ label, value, sub, mono, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className={cn("mt-1 font-semibold break-words", mono ? "hs-mono text-lg" : "text-[15px]")}>{value}</dd>
      {sub && <dd className="text-xs text-muted-foreground mt-0.5">{sub}</dd>}
    </div>
  );
}

export function Note({ tone = "amber", children, testId }) {
  const t = tone === "rose" ? "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-200" : "border-amber-500/25 bg-amber-500/10 text-amber-800 dark:text-amber-200";
  return <div className={cn("rounded-lg border px-3.5 py-2.5 text-sm leading-relaxed", t)} data-testid={testId}>{children}</div>;
}
